import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { systemDesignApi } from "../api/systemDesignApi";
import { SystemDesign } from "../types/SystemDesign";
import { ArrowLeft, Edit3, Building2, Image as ImageIcon } from "lucide-react";

const SystemDesignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [design, setDesign] = useState<SystemDesign | null>(null);

  useEffect(() => {
    fetchDesign();
  }, [id]);

  const fetchDesign = async () => {
    try {
      const fetchedDesign = await systemDesignApi.getById(Number(id));
      setDesign(fetchedDesign);
    } catch (error) {
      console.error("Error fetching system design:", error);
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case "easy": return "bg-emerald-500/15 text-emerald-400 border-emerald-500/20";
      case "medium": return "bg-amber-500/15 text-amber-400 border-amber-500/20";
      case "hard": return "bg-red-500/15 text-red-400 border-red-500/20";
      default: return "bg-gray-500/15 text-gray-400 border-gray-500/20";
    }
  };

  if (!design) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${design.title} - System Design - AlgoBuddy`}</title>
        <meta
          name="description"
          content={`Learn about ${design.title} system design. Company: ${design.company}. Difficulty: ${design.difficulty}.`}
        />
      </Helmet>

      <main className="min-h-screen bg-gray-950">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-gray-950/80 backdrop-blur-lg border-b border-gray-800/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <button
              onClick={() => navigate("/system-design")}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              System Design
            </button>
            <button
              onClick={() => navigate(`/system-design/edit/${design.id}`)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-800 hover:border-gray-700 rounded-lg transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Edit
            </button>
          </div>
        </div>

        {/* Article */}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
          {/* Title section */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/20 rounded-md">
                <Building2 className="w-3.5 h-3.5" />
                {design.company}
              </span>
              <span
                className={`px-2.5 py-1 text-xs font-medium border rounded-md ${getDifficultyColor(
                  design.difficulty
                )}`}
              >
                {design.difficulty}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight">
              {design.title}
            </h1>
          </header>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-10" />

          {/* Content */}
          <div className="space-y-8">
            <div className="text-gray-300 text-lg leading-[1.85] whitespace-pre-wrap font-light">
              {design.description}
            </div>

            {/* Images */}
            {design.images && design.images.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-gray-500 font-medium uppercase tracking-wider">
                  <ImageIcon className="w-4 h-4" />
                  Diagrams
                </div>
                <div className="space-y-4">
                  {design.images.map((url, index) => (
                    <figure key={index} className="group">
                      <img
                        src={url}
                        alt={`${design.title} diagram ${index + 1}`}
                        className="w-full rounded-xl border border-gray-800 hover:border-gray-700 transition-colors"
                      />
                      <figcaption className="mt-2 text-sm text-gray-500">
                        Figure {index + 1}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
    </>
  );
};

export default SystemDesignDetailPage;
